import jsPDF from 'jspdf';
import JsBarcode from 'jsbarcode';
import { getCurrencySymbol } from '../../../util/productUtils';

// One physical label to print. `code` is the barcode payload — the SKU for
// non-serial-tracked lines (same code repeated across `quantity` copies), or
// a single unique serial number for serial-tracked lines (one item per unit).
export interface LabelQueueItem {
  productName: string;
  variantText?: string;
  code: string;
  isSerial: boolean;
  price?: number;
}

export interface LabelLayoutOptions {
  labelWidthMm: number;
  labelHeightMm: number;
  columns: number;
  includePrice: boolean;
  currency?: string;
}

export class LabelPDFGenerator {
  private static readonly PAGE_MARGIN_MM = 10;
  private static readonly GUTTER_MM = 3;

  // Renders a CODE128 barcode to an off-screen canvas and reads it back out
  // as a PNG data URL — same offscreen-render-then-embed technique
  // reportChartRenderer.tsx uses with html2canvas for report charts.
  private static renderBarcode(code: string): { dataUrl: string; pixelWidth: number; pixelHeight: number } {
    const canvas = document.createElement('canvas');
    JsBarcode(canvas, code, {
      format: 'CODE128',
      displayValue: true,
      fontSize: 14,
      height: 40,
      margin: 6,
    });
    return { dataUrl: canvas.toDataURL('image/png'), pixelWidth: canvas.width, pixelHeight: canvas.height };
  }

  /** How many pages a given queue + layout will produce — used for the live page-count hint in the UI. */
  static countPages(queueLength: number, options: LabelLayoutOptions): number {
    if (queueLength === 0) return 0;
    const rows = Math.ceil(queueLength / options.columns);
    const usableHeight = 297 - this.PAGE_MARGIN_MM * 2; // A4 portrait, mm
    const rowsPerPage = Math.max(1, Math.floor(usableHeight / (options.labelHeightMm + this.GUTTER_MM)));
    return Math.max(1, Math.ceil(rows / rowsPerPage));
  }

  static generatePDF(queue: LabelQueueItem[], options: LabelLayoutOptions): void {
    if (queue.length === 0) return;
    const sym = options.currency ? getCurrencySymbol(options.currency) : '';

    const doc = new jsPDF();
    const pw = doc.internal.pageSize.getWidth();
    const ph = doc.internal.pageSize.getHeight();

    const { labelWidthMm, labelHeightMm, columns, includePrice } = options;
    const cellWidth = (pw - this.PAGE_MARGIN_MM * 2) / columns;
    const rowsPerPage = Math.max(1, Math.floor((ph - this.PAGE_MARGIN_MM * 2) / (labelHeightMm + this.GUTTER_MM)));

    let col = 0;
    let row = 0;

    queue.forEach((item) => {
      if (row >= rowsPerPage) {
        doc.addPage();
        row = 0;
        col = 0;
      }

      const x = this.PAGE_MARGIN_MM + col * cellWidth;
      const y = this.PAGE_MARGIN_MM + row * (labelHeightMm + this.GUTTER_MM);
      this.drawLabel(doc, item, x, y, labelWidthMm, labelHeightMm, includePrice, sym);

      col += 1;
      if (col >= columns) {
        col = 0;
        row += 1;
      }
    });

    const totalPages = doc.internal.pages.length - 1;
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(128, 128, 128);
      doc.text(`Page ${i} of ${totalPages}`, pw / 2, ph - 6, { align: 'center' });
    }

    doc.save(`labels_${Date.now()}.pdf`);
  }

  private static drawLabel(
    doc: jsPDF,
    item: LabelQueueItem,
    x: number,
    y: number,
    w: number,
    h: number,
    includePrice: boolean,
    sym: string
  ): void {
    doc.setDrawColor(200);
    doc.rect(x, y, w, h);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(30, 30, 30);
    const nameLine = item.variantText ? `${item.productName} (${item.variantText})` : item.productName;
    const wrapped: string[] = doc.splitTextToSize(nameLine, w - 4).slice(0, 2);
    let cursorY = y + 4.5;
    doc.text(wrapped, x + w / 2, cursorY, { align: 'center' });
    cursorY += wrapped.length * 3.2 + 1;

    const priceReserved = includePrice && item.price !== undefined ? 5 : 1;
    const availableHeight = Math.max(6, h - (cursorY - y) - priceReserved);
    const { dataUrl, pixelWidth, pixelHeight } = this.renderBarcode(item.code);

    let barcodeHeight = Math.min(availableHeight, 14);
    let barcodeWidth = barcodeHeight * (pixelWidth / pixelHeight);
    const maxWidth = w - 6;
    if (barcodeWidth > maxWidth) {
      const scale = maxWidth / barcodeWidth;
      barcodeWidth = maxWidth;
      barcodeHeight *= scale;
    }
    doc.addImage(dataUrl, 'PNG', x + (w - barcodeWidth) / 2, cursorY, barcodeWidth, barcodeHeight);

    if (includePrice && item.price !== undefined) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      const priceText = `${sym} ${item.price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      doc.text(priceText, x + w / 2, y + h - 1.5, { align: 'center' });
    }
  }
}
